import classNames from 'clsx';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';
import { useDispatch, useSelector } from 'react-redux';

import { Alert } from 'app/components/Alert';
import { ControlContainer } from 'app/components/ControlContainer';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { DictionaryEntry } from 'app/services/dict.service';
import { MessageTypeEnum, PaymentMethodCodeEnum } from 'app/shared/enums';
import { PaymentMethodEnum } from 'app/shared/enums';
import { useRadioGroupControl } from 'app/shared/hooks/controls/useRadioGroupControl';
import { scrollToNextQuestion } from 'app/store/data/formsConfig';
import { selectIsPaymentMethodOnInstallmentsActive } from 'app/store/data/quotationResult/selectors';
import { LargeRadioWithIconControlProps } from './LargeRadioWithIconControl.interface';
import { useStyles } from './LargeRadioWithIconControl.styles';

export default function LargeRadioWithIconControl(
  props: LargeRadioWithIconControlProps
) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    input,
    isLoading,
    width,
    label,
    subLabel,
    helperText,
    tooltip,
    meta,
    formName,
  } = props;
  const isPaymentMethodOnInstallmentsActive = useSelector(
    selectIsPaymentMethodOnInstallmentsActive
  );

  const {
    bulbInfoMessage,
    handleChange,
    handleClick,
    renderRadioButtons,
  } = useRadioGroupControl(props);

  const handleValueChangeWithScroll = (value: string) => {
    const shouldScroll = String(input.value ?? '') !== value;
    handleChange(value);

    if (shouldScroll && formName && props.questionKey) {
      setTimeout(() => {
        dispatch(scrollToNextQuestion(formName, props.questionKey));
      });
    }
  };

  const renderIcon = (paymentMethod: PaymentMethodEnum) => {
    if (!paymentMethod) {
      return null;
    }

    const iconSrcMap: Partial<
      Record<PaymentMethodEnum | PaymentMethodCodeEnum, string>
    > = {
      [PaymentMethodEnum.Blik]: './images/payment-method/blik_trimmed.svg',
      [PaymentMethodEnum.OnlineTransfer]: './images/payment-method/ing_pay.svg',
      [PaymentMethodEnum.Card]: './images/payment-method/mc_visa.svg',
      [PaymentMethodCodeEnum.PayPo]: './images/payment-method/paypo.svg',
      [PaymentMethodCodeEnum.GPay]:
        './images/payment-method/google_pay_circle.svg',
      [PaymentMethodCodeEnum.ApplePay]:
        './images/payment-method/apple_pay_trimmed.svg',
    };

    const iconSrc = iconSrcMap[paymentMethod];
    if (!iconSrc) {
      return null;
    }

    return <img className={classes.iconImage} src={iconSrc} alt={''} />;
  };

  const renderRadioButton = (entry: DictionaryEntry) => {
    const { key } = entry;
    const isActive = key === String(input.value ?? '');

    return (
      <FormControlLabel
        tabIndex={0}
        key={key}
        value={key}
        className={classNames(classes.option, 'focusVisibleOutline', {
          [classes.optionActive]: isActive,
        })}
        classes={{
          label: classes.label,
        }}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (event.key === 'Enter' || event.key === ' ')
          ) {
            event.preventDefault();
            handleValueChangeWithScroll(String(key));
          }
        }}
        control={
          <Radio
            className={classes.radio}
            color="primary"
            checkedIcon={<RadioboxMarked />}
            onClick={handleClick}
            inputProps={{
              tabIndex: -1,
            }}
          />
        }
        label={
          <>
            {renderIcon(entry.key as PaymentMethodEnum)}
            <span>{entry.value}</span>
          </>
        }
      />
    );
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <ControlContainer
        withoutHtmlFor
        inputId={input.name}
        formName={formName}
        width={width}
        label={label}
        subLabel={subLabel}
        helperText={helperText}
        tooltip={tooltip}
        meta={meta}
        isPaymentMethodOnInstallmentsActive={
          isPaymentMethodOnInstallmentsActive
        }
        bottomTipMessage={
          bulbInfoMessage ? (
            <Alert
              text={bulbInfoMessage.content}
              type={MessageTypeEnum.BulbInfo}
              className={classes.bottomInfoMessage}
              icon={false}
            />
          ) : undefined
        }
      >
        <FormControl>
          <RadioGroup
            name={input.name}
            id={input.name}
            aria-labelledby={`${input.name}-label`}
            aria-label={label}
            value={String(input.value ?? '')}
            className={classes.radioGroup}
            onChange={(event, value) => handleValueChangeWithScroll(value)}
          >
            {renderRadioButtons(renderRadioButton)}
          </RadioGroup>
        </FormControl>
      </ControlContainer>
    </ReactPlaceholder>
  );
}
